import type { ComponentProps } from 'react'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { PageHeader } from '@/components/PageHeader'
import { statusLabel } from '@/components/issues/issue-utils'
import type { IssueStatus, ReportIssueSummary } from '@headline/schemas'
import { Badge } from '@/components/ui/badge'
import { DiscardDraftButton } from '@/components/issues/DiscardDraftButton'
import { NewDraftButton } from '@/components/issues/NewDraftButton'
import { issuesQuery, useDeleteIssue } from '@/lib/issue-queries'
import { reportQuery } from '@/lib/queries'

/**
 * A report's previous reports — the dated instances an associate edits and sends (issues, in the
 * data model). Newest first; drafts can be deleted, sent ones can't (they are the record of what
 * went out) — and a draft Headline prepares for a send time is skipped rather than deleted, after
 * a confirmation (`DiscardDraftButton`, CLI-982). "New draft" creates today's with the default 24-hour window and opens it; pulling is
 * the editor's first action.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/reports/$reportId_/issues/')({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(reportQuery(params.reportId)),
      context.queryClient.ensureQueryData(issuesQuery(params.reportId)),
    ])
  },
  component: IssuesPage,
})

function IssuesPage() {
  const { accountId, reportId } = Route.useParams()
  const { data: report } = useSuspenseQuery(reportQuery(reportId))
  const { data } = useSuspenseQuery(issuesQuery(reportId))
  const queryClient = useQueryClient()
  const deleteIssue = useDeleteIssue(reportId, queryClient)

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <Link
          to="/app/accounts/$accountId/reports/$reportId"
          params={{ accountId, reportId }}
          className="flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="size-3.5" />
          {report.name} configuration
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <PageHeader
            title={`${report.name} — Previous reports`}
            description="Each row is one day's report, a draft in progress or one that went out. Pull the window's coverage, curate it header by header, preview, send."
            className="mb-0"
          />
          <div className="flex flex-col items-end gap-1">
            <NewDraftButton
              report={{
                id: reportId,
                accountId,
                sendMode: report.settings.delivery.sendMode,
                hasHeaders: report.sections.length > 0,
              }}
            />
          </div>
        </div>
      </div>
      {deleteIssue.isError && (
        <p role="alert" className="text-sm text-destructive">
          {deleteIssue.error.message}
        </p>
      )}

      {data.issues.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing yet. Start a new draft to pull today’s coverage into this report’s headers.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">Status</th>
                <th className="px-3 py-2 text-right font-medium">Articles</th>
                <th className="px-3 py-2 text-left font-medium">Window</th>
                <th className="px-3 py-2 text-left font-medium">Sent</th>
                <th className="px-3 py-2 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data.issues.map((issue) => (
                <IssueRow
                  key={issue.id}
                  issue={issue}
                  accountId={accountId}
                  reportId={reportId}
                  deleting={deleteIssue.isPending}
                  onDelete={() => deleteIssue.mutate(issue.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

/** The badge colour each status reads as; anything unlisted (a draft) is `secondary`. */
const STATUS_VARIANT: Partial<Record<IssueStatus, ComponentProps<typeof Badge>['variant']>> = {
  sent: 'success',
  scheduled: 'warning',
  failed: 'destructive',
  skipped: 'neutral',
}

const WINDOW_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
}

interface IssueRowProps {
  issue: ReportIssueSummary
  accountId: string
  reportId: string
  deleting: boolean
  onDelete: () => void
}

function IssueRow({ issue, accountId, reportId, deleting, onDelete }: IssueRowProps) {
  return (
    <tr className="border-t border-border hover:bg-accent/30">
      <td className="px-3 py-2">
        <Link
          to="/app/accounts/$accountId/reports/$reportId/issues/$issueId"
          params={{ accountId, reportId, issueId: issue.id }}
          className="font-medium text-foreground underline-offset-4 hover:underline"
        >
          {new Date(`${issue.issueDate}T00:00:00`).toLocaleDateString(undefined, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Link>
      </td>
      <td className="px-3 py-2">
        <Badge
          variant={STATUS_VARIANT[issue.status] ?? 'secondary'}
          title={issue.lastError ?? undefined}
        >
          {statusLabel(issue.status)}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {issue.pulledAt === null && issue.itemCount === 0 ? (
          <span className="text-muted-foreground">not pulled</span>
        ) : (
          issue.itemCount
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {new Date(issue.windowFrom).toLocaleString(undefined, WINDOW_FORMAT)} →{' '}
        {new Date(issue.windowTo).toLocaleString(undefined, WINDOW_FORMAT)}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        <SentCell issue={issue} />
      </td>
      <td className="px-3 py-2 text-right">
        {issue.status === 'draft' && (
          <DiscardDraftButton issue={issue} pending={deleting} onDiscard={onDelete} />
        )}
      </td>
    </tr>
  )
}

function SentCell({ issue }: { issue: ReportIssueSummary }) {
  if (issue.sentAt) {
    // One line per send, newest first — an issue sent three times reads as
    // three lines, not one date with a badge (Kevin, 2026-09-30).
    return (
      <ul className="flex flex-col gap-0.5">
        {(issue.sentAts.length > 0 ? [...issue.sentAts].reverse() : [issue.sentAt]).map((at) => (
          <li key={at} className="whitespace-nowrap tabular-nums">
            {new Date(at).toLocaleString()}
          </li>
        ))}
      </ul>
    )
  }
  return issue.scheduledFor ? `Scheduled ${new Date(issue.scheduledFor).toLocaleString()}` : '—'
}
